import { useToggle } from '@dealstack/frontend/shared/components/ExpandablePanel/useToggle';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import {
	CreateVestingTermStraightLineDto,
	UpdateVestingTermStraightLineDto,
	VestingTermStraightLineRes,
	VestingTermTimeUnitType,
} from '@dealstack/shared/lib/generated';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	MAX_MONTHLY_PERIOD,
	MAX_YEARLY_PERIOD,
} from '@dealstack/shared/lib/common/business/vestingTerms.constants';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

const getStraightLineFormShape = (t: TranslateFunction<I18Namespaces.COMMON_UI>) =>
	z
		.object({
			cliff: z
				.number({ message: t('vesting_terms.error.straight_line.cliff.number') })
				.int(t('vesting_terms.error.straight_line.cliff.int'))
				.positive(t('vesting_terms.error.straight_line.cliff.positive'))
				.optional()
				.nullable(),
			interval: z
				.number({
					invalid_type_error: t('vesting_terms.error.straight_line.interval.number'),
					required_error: t('vesting_terms.error.straight_line.interval.required'),
				})
				.int(t('vesting_terms.error.straight_line.interval.int'))
				.positive(t('vesting_terms.error.straight_line.interval.positive')),
			period: z
				.number({
					invalid_type_error: t('vesting_terms.error.straight_line.period.number'),
					required_error: t('vesting_terms.error.straight_line.period.required'),
				})
				.int(t('vesting_terms.error.straight_line.period.int'))
				.positive(t('vesting_terms.error.straight_line.period.positive')),
			periodTimeUnit: z.nativeEnum(VestingTermTimeUnitType),
		})
		.refine((data) => data.period >= data.interval, {
			message: t('vesting_terms.error.straight_line.period.less_than_interval'),
			path: ['period'],
		})
		.refine((data) => data.period > (data.cliff ?? 0), {
			message: t('vesting_terms.error.straight_line.period.less_than_cliff'),
			path: ['period'],
		})
		.refine(
			(data) => {
				if (
					data.periodTimeUnit === VestingTermTimeUnitType.MONTHLY &&
					data.period > MAX_MONTHLY_PERIOD
				) {
					return false;
				}
				if (
					data.periodTimeUnit === VestingTermTimeUnitType.YEARLY &&
					data.period > MAX_YEARLY_PERIOD
				) {
					return false;
				}
				return true;
			},
			(data) => ({
				message: t('vesting_terms.error.straight_line.period.max_exceeded', {
					maxValue:
						data.periodTimeUnit === VestingTermTimeUnitType.MONTHLY
							? MAX_MONTHLY_PERIOD
							: MAX_YEARLY_PERIOD,
					timeUnit: data.periodTimeUnit.toLocaleString(),
				}),
				path: ['period'],
			})
		);

export const useStraightLineTermController = (
	onCreateVestingTermStraightLine: (
		vestingTermStraightLine: CreateVestingTermStraightLineDto
	) => void,
	onUpdateVestingTermStraightLine: (
		straightLineTermId: string,
		formValues: UpdateVestingTermStraightLineDto
	) => void,
	vestingTermId: string,
	vestingTermStraightLine?: VestingTermStraightLineRes
) => {
	const tCommonUi = useTranslationCommonUi();

	const { isExpanded, toggleExpanded } = useToggle();

	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm({
		mode: 'all',
		defaultValues: {
			cliff: vestingTermStraightLine?.cliff,
			interval: vestingTermStraightLine?.interval ?? 1,
			period: vestingTermStraightLine?.period ?? 1,
			periodTimeUnit:
				vestingTermStraightLine?.periodTimeUnit ?? VestingTermTimeUnitType.MONTHLY,
		},
		resolver: zodResolver(getStraightLineFormShape(tCommonUi)),
	});

	const onSubmit = (data: UpdateVestingTermStraightLineDto) => {
		if (vestingTermStraightLine) {
			onUpdateVestingTermStraightLine(vestingTermStraightLine.id, data);
		} else {
			onCreateVestingTermStraightLine({ ...data, vestingTermId });
		}

		if (isExpanded) {
			toggleExpanded();
		}
	};

	const onCancel = () => {
		toggleExpanded();
	};

	return {
		onSubmit,
		onCancel,
		isExpanded,
		toggleExpanded,
		handleSubmit,
		control,
		setValue,
		errors,
	};
};
